import { apiClient } from './apiClient';

export type ManagementSearchType = '0' | '1' | '2' | '3' | '4' | '5';
export type MembershipFilterStatus = 'active' | 'added' | 'declined' | 'cancelled' | 'frozen' | 'cancelling';

export interface SelectValueOption {
  value: number;
  label: string;
}

export interface MembershipManagementOptions {
  templates: SelectValueOption[];
  facilities: SelectValueOption[];
}

export interface MembershipManagementFilters {
  page: number;
  perPage: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search?: string;
  searchType?: ManagementSearchType;
  dateFrom?: string;
  dateTo?: string;
  templateId?: number;
  /** Multi-value template filter; mirrors legacy `filtersArray[membership-type]`. */
  templateIds?: number[];
  /** When true, server applies `NOT IN` for templateIds; mirrors legacy `filterPlus=false`. */
  templateIdsNegate?: boolean;
  status?: MembershipFilterStatus;
  statuses?: MembershipFilterStatus[];
  statusesNegate?: boolean;
  facilityId?: number;
  facilityIds?: number[];
  facilityIdsNegate?: boolean;
  export?: boolean;
  /** Unified filtersArray JSON — when present, backend ignores individual filter params. */
  filtersArray?: string;
}

/**
 * Legacy-parity stats (the `$totalStatsCount` strip legacy renders above the list).
 * Each count is the filtered set plus one condition, so they are NOT a partition of
 * totalCount: `addedCount` keys off the create date, `cancelledCount` off the cancel
 * date, and frozen/declined off the current billing period.
 */
export interface MembershipManagementStats {
  /** Total in the filtered set — memberships on the memberships tab, patients on members. */
  totalCount: number;
  activeCount: number;
  /** Created within the selected date range (all of them when no range is set). */
  addedCount: number;
  /** Cancelled within the selected date range. */
  cancelledCount: number;
  frozenCount: number;
  /** Past due. */
  declinedCount: number;
}

export interface MembershipManagementRow {
  enrollmentId: number;
  patientId: number;
  patientName: string;
  templateId: number;
  type: string;
  amount: number;
  status: MembershipFilterStatus;
  notes: string | null;
  createdAt: string | null;
  nextChargeDate: string | null;
  startDate: string | null;
  endDate: string | null;
  pauseUntilDate: string | null;
  recurringPaymentId: number | null;
  paymentId: number | null;
  facilityId: number | null;
  userName: string | null;
}

export interface MembershipMemberRow {
  patientId: number;
  patientName: string;
  memberships: string;
  membershipCount: number;
  createdAt: string | null;
  startDate: string | null;
  paidUntilDate: string | null;
  cancelDate: string | null;
  daysActive: number;
}

export interface PaginatedResult<T, TStats> {
  items: T[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  sort: {
    field: string;
    direction: 'asc' | 'desc';
  };
  stats: TStats;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return fallback;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function isZeroDateString(value: string): boolean {
  return value === '0000-00-00' || value === '0000-00-00 00:00:00';
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' && !isZeroDateString(value.trim()) ? value : null;
}

function cleanParams(filters: Partial<MembershipManagementFilters>) {
  const entries = Object.entries(filters).filter(([, value]) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  });

  return Object.fromEntries(entries);
}

export async function getMembershipManagementOptions(): Promise<MembershipManagementOptions> {
  const res = await apiClient.get<{ data: { templates?: unknown[]; facilities?: unknown[] } }>(
    '/memberships/management/options',
  );

  return {
    templates: (res.data.data.templates ?? []).map((item) => ({
      value: asNumber((item as Record<string, unknown>).value),
      label: asString((item as Record<string, unknown>).label),
    })),
    facilities: (res.data.data.facilities ?? []).map((item) => ({
      value: asNumber((item as Record<string, unknown>).value),
      label: asString((item as Record<string, unknown>).label),
    })),
  };
}

export async function getMembershipManagementEnrollments(
  filters: MembershipManagementFilters,
): Promise<PaginatedResult<MembershipManagementRow, MembershipManagementStats>> {
  const res = await apiClient.get<{ data: PaginatedResult<Record<string, unknown>, Record<string, unknown>> }>(
    '/memberships/management/enrollments',
    { params: cleanParams(filters) },
  );

  const payload = res.data.data;

  return {
    items: (payload.items ?? []).map((row) => ({
      enrollmentId: asNumber(row.enrollmentId),
      patientId: asNumber(row.patientId),
      patientName: asString(row.patientName),
      templateId: asNumber(row.templateId),
      type: asString(row.type),
      amount: asNumber(row.amount),
      status: (asString(row.status, 'active') as MembershipFilterStatus),
      notes: asNullableString(row.notes),
      createdAt: asNullableString(row.createdAt),
      nextChargeDate: asNullableString(row.nextChargeDate),
      startDate: asNullableString(row.startDate),
      endDate: asNullableString(row.endDate),
      pauseUntilDate: asNullableString(row.pauseUntilDate),
      recurringPaymentId: row.recurringPaymentId == null ? null : asNumber(row.recurringPaymentId),
      paymentId: row.paymentId == null ? null : asNumber(row.paymentId),
      facilityId: row.facilityId == null ? null : asNumber(row.facilityId),
      userName: asNullableString(row.userName),
    })),
    pagination: {
      page: asNumber(payload.pagination?.page, 1),
      perPage: asNumber(payload.pagination?.perPage, filters.perPage),
      total: asNumber(payload.pagination?.total, 0),
    },
    sort: {
      field: asString(payload.sort?.field, filters.sortBy),
      direction: asString(payload.sort?.direction, filters.sortDir) === 'asc' ? 'asc' : 'desc',
    },
    stats: {
      totalCount: asNumber(payload.stats?.totalCount),
      activeCount: asNumber(payload.stats?.activeCount),
      addedCount: asNumber(payload.stats?.addedCount),
      cancelledCount: asNumber(payload.stats?.cancelledCount),
      frozenCount: asNumber(payload.stats?.frozenCount),
      declinedCount: asNumber(payload.stats?.declinedCount),
    },
  };
}

export async function getMembershipManagementMembers(
  filters: MembershipManagementFilters,
): Promise<PaginatedResult<MembershipMemberRow, MembershipManagementStats>> {
  const res = await apiClient.get<{ data: PaginatedResult<Record<string, unknown>, Record<string, unknown>> }>(
    '/memberships/management/members',
    { params: cleanParams(filters) },
  );

  const payload = res.data.data;

  return {
    items: (payload.items ?? []).map((row) => ({
      patientId: asNumber(row.patientId),
      patientName: asString(row.patientName),
      memberships: asString(row.memberships),
      membershipCount: asNumber(row.membershipCount),
      createdAt: asNullableString(row.createdAt),
      startDate: asNullableString(row.startDate),
      paidUntilDate: asNullableString(row.paidUntilDate),
      cancelDate: asNullableString(row.cancelDate),
      daysActive: asNumber(row.daysActive),
    })),
    pagination: {
      page: asNumber(payload.pagination?.page, 1),
      perPage: asNumber(payload.pagination?.perPage, filters.perPage),
      total: asNumber(payload.pagination?.total, 0),
    },
    sort: {
      field: asString(payload.sort?.field, filters.sortBy),
      direction: asString(payload.sort?.direction, filters.sortDir) === 'asc' ? 'asc' : 'desc',
    },
    stats: {
      totalCount: asNumber(payload.stats?.totalCount),
      activeCount: asNumber(payload.stats?.activeCount),
      addedCount: asNumber(payload.stats?.addedCount),
      cancelledCount: asNumber(payload.stats?.cancelledCount),
      frozenCount: asNumber(payload.stats?.frozenCount),
      declinedCount: asNumber(payload.stats?.declinedCount),
    },
  };
}
